import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate, formatPhoneNumber } from '@strata/helper-functions';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    type ColumnDef,
    GridWithFilters,
    PrimaryData,
    SecondaryData,
    type TimeFrameConfig,
    TimeFrameOptions,
} from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import dayjs from 'dayjs';

import { ActiveIntakeSourceActionsMenu } from 'components/intakeSources/ActiveIntakeSourceActionsMenu';
import { CustomTimeFrameOptions } from 'services/constants';
import { type TimeFrameOption } from 'services/interfaces';
import { type IntakeSourceSummary, useGetIntakeSourcesQuery } from 'store/api/intakeCaseApi';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridsThunks';

const InitialSortProperties = [{ id: 'received_date', desc: true }];

const timeFrameConfig: TimeFrameConfig = {
    key: 'received_date',
    title: 'Time Frame',
    defaultValue: TimeFrameOptions.PAST_WEEK,
    optionOverrideMap: {
        [CustomTimeFrameOptions.ALL_TIME]: { text: 'All', handler: () => true },
    },
};

const columns: ColumnDef<IntakeSourceSummary>[] = [
    {
        accessorKey: 'fax_number',
        header: 'Fax Number',
        size: 150,
        cell: ({ getValue }) => <PrimaryData data={formatPhoneNumber(getValue())} />,
    },
    {
        accessorKey: 'sent_by',
        header: 'Sent By',
        size: 150,
    },
    {
        accessorKey: 'received_date',
        header: 'Received Date',
        size: 150,
        cell: ({ getValue }) => {
            const daysInQueue = dayjs().diff(getValue(), 'days');
            return (
                <span className="flex__gap--small">
                    <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
                    {daysInQueue >= 3 && (
                        <Icon
                            intent={Icon.Intent.WARNING}
                            icon={IconNames.WARNING_SIGN}
                            tooltipContent={`Fax has been in the queue for ${daysInQueue} days`}
                        />
                    )}
                </span>
            );
        },
    },
    {
        accessorKey: 'intake_source_id',
        header: () => null,
        enableSorting: false,
        size: 48,
        cell: ({ getValue, row }) => (
            <ActiveIntakeSourceActionsMenu
                intakeSourceId={getValue()}
                intakeSource={row.original}
            />
        ),
    },
];

export const ActiveIntakeSourcesGrid = () => {
    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        page,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
        updateCurrentPage,
    } = useGridState(Grids.ActiveIntakeSources, InitialSortProperties);

    const apiQueryParams = {
        page,
        searchText: search,
        timeframe: activeFilters?.received_date?.[0] as TimeFrameOption,
        archived: false,
        sort,
    };
    // The filter pane reports the time frame's default value on mount, so activeFilters is
    // undefined for the first render. Wait for it rather than querying an unfiltered time frame
    // and refetching once the default lands.
    const { data: firstPageData, isFetching: isFirstPageDataFetching } = useGetIntakeSourcesQuery(
        { ...apiQueryParams, page: 0 },
        { skip: !activeFilters },
    );
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } =
        useGetIntakeSourcesQuery(apiQueryParams, { skip: !activeFilters });

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Active Intake Sources"
                data={currentPageData?.intakeSources}
                isLoading={!activeFilters || isFirstPageDataFetching || isCurrentPageDataFetching}
                columns={columns}
                manualSorting
                sortProperties={sort}
                onSort={updateGridSort}
                enableSearch
                searchText={search}
                onSearch={updateGridSearch}
                timeFrameConfig={timeFrameConfig}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                currentPage={page}
                onPageChange={updateCurrentPage}
                totalCount={firstPageData?.count}
            />
        </FormPageContainer>
    );
};
